import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Container, Typography, Grid, CircularProgress, Button, TextField, FormControlLabel, Checkbox, Tabs, Tab, Snackbar, Alert, Paper, IconButton } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignService, donationService } from '../services/api';
import { FormatQuote, Favorite, CalendarToday, InsertDriveFile, OpenInNew, Add, Remove } from '@mui/icons-material';
import { getImageUrl, getApiOrigin } from '../utils/assets';
import { ASSETS } from '../utils/assets';
import { BRAND, COLORS, primaryButtonSx, bodyTextSx, cardSx, cardHeadingSx, sectionPaddingSx, sectionSubtitleSx, sectionHeadingSx } from '../theme/pageStyles';
import CampaignShareButtons from '../components/CampaignShareButtons';
import ImageHeroBanner from '../components/ImageHeroBanner';
import CampaignRatingsSection from '../components/CampaignRatingsSection';
import SiteStatsItems from '../components/SiteStatsItems';

const PURPLE_ACCENT = '#7B2FBB';
const QUOTE_PANEL_PURPLE = '#6F2DA8';
const PURPLE_LIGHT = '#9f5dd4';
const DARK_BG = '#2D2F2D';
const PROGRESS_GRAY = '#4a4a4a';
const STATS_BAR_BG = '#3d3d3d';
const STATS_BAR_BORDER = '#5c3317';
const STATS_LABEL_GRAY = 'rgba(255,255,255,0.65)';

/** Sponsor kit grid — background per slot (reference: white / mint / blush / mint / blush / cream) */
const SPONSOR_TIER_CARD_BGS = ['#ffffff', '#d8f0dc', '#fde8e8', '#d8f0dc', '#fde8e8', '#fff8dc'];

const essentialsSectionBoxSx = {
  borderRadius: 2,
  overflow: 'hidden',
  border: '1px solid #b8dfd9',
  boxShadow: '0 4px 18px rgba(13, 138, 126, 0.12)',
  backgroundColor: '#fafcfb',
};

const essentialsHeaderSx = {
  background: 'linear-gradient(180deg, #e8f7f4 0%, #dff2ee 100%)',
  py: 1.75,
  px: 2,
  textAlign: 'center',
  borderBottom: '2px solid #9dd5cd',
};

const essentialsTitleSx = {
  fontFamily: '"Poppins", sans-serif',
  fontWeight: 800,
  fontSize: { xs: 15, sm: 17 },
  letterSpacing: '0.14em',
  color: '#0d7d72',
  lineHeight: 1.3,
};

/** Donate Essentials — same card grid as Child Education Support, with quantity − / + on each card. */
function DonateEssentialsBlock({ essentialRows, essentialsQty, bumpEssential, essentialsExtraTotal, description, sx }) {
  if (!essentialRows?.length) return null;

  return (
    <Box sx={{ ...essentialsSectionBoxSx, ...sx }}>
      <Box sx={essentialsHeaderSx}>
        <Typography sx={essentialsTitleSx}>DONATE ESSENTIALS</Typography>
      </Box>
      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
        <Typography sx={{ ...bodyTextSx, fontSize: 14, mb: 2, color: 'text.secondary', textAlign: 'center' }}>
          {description}
        </Typography>
        <Grid container spacing={1.5}>
          {essentialRows.map((row, idx) => {
            const qty = Number(essentialsQty[row.id]) || 0;
            const selected = qty > 0;
            const cardBg = SPONSOR_TIER_CARD_BGS[idx % SPONSOR_TIER_CARD_BGS.length];
            return (
              <Grid item xs={12} sm={6} md={4} key={row.id}>
                <Paper
                  elevation={selected ? 4 : 0}
                  onClick={() => bumpEssential(row.id, 1)}
                  sx={{
                    p: 2.25,
                    minHeight: 160,
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: selected ? `3px solid ${BRAND.green}` : '1px solid rgba(0,0,0,0.08)',
                    backgroundColor: cardBg,
                    boxShadow: selected ? '0 0 0 3px rgba(36, 136, 78, 0.25)' : 'none',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 800,
                      fontSize: { xs: 12, sm: 13 },
                      letterSpacing: '0.04em',
                      lineHeight: 1.35,
                      textAlign: 'center',
                      color: '#1a1a1a',
                      mb: 1,
                      textTransform: 'uppercase',
                    }}
                  >
                    {row.label}
                  </Typography>
                  {row.description && (
                    <Typography
                      sx={{
                        fontFamily: '"Nunito", sans-serif',
                        fontSize: { xs: 12, sm: 13 },
                        fontWeight: 600,
                        textAlign: 'center',
                        color: '#4a4a4a',
                        lineHeight: 1.4,
                        mb: 0.75,
                      }}
                    >
                      {row.description}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontFamily: '"Nunito", sans-serif',
                      fontSize: { xs: 13, sm: 14 },
                      fontWeight: 700,
                      textAlign: 'center',
                      color: '#2d2d2d',
                      lineHeight: 1.45,
                      mb: 1.5,
                    }}
                  >
                    Rs. {row.unitPrice.toLocaleString('en-IN')}/-
                  </Typography>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      onClick={() => bumpEssential(row.id, -1)}
                      aria-label={`Decrease ${row.label}`}
                      sx={{
                        border: '1px solid rgba(13, 125, 114, 0.35)',
                        borderRadius: 1,
                        backgroundColor: '#fff',
                        '&:hover': { backgroundColor: '#e8f7f4' },
                      }}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 800, fontFamily: '"Nunito", sans-serif' }}>
                      {qty}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => bumpEssential(row.id, 1)}
                      aria-label={`Increase ${row.label}`}
                      sx={{
                        border: '1px solid rgba(13, 125, 114, 0.35)',
                        borderRadius: 1,
                        backgroundColor: '#fff',
                        '&:hover': { backgroundColor: '#e8f7f4' },
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        {essentialsExtraTotal > 0 && (
          <Typography
            sx={{
              mt: 2,
              fontFamily: '"Nunito", sans-serif',
              fontWeight: 800,
              color: BRAND.green,
              fontSize: 16,
              textAlign: 'center',
            }}
          >
            Essentials subtotal: ₹{essentialsExtraTotal.toLocaleString('en-IN')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** PHP APIs return TINYINT as "1"/"0"; Node/mysql2 often returns 1/0. Treat all as active. */
function isEssentialActive(value) {
  if (value === undefined || value === null || value === '') return true;
  return value === 1 || value === true || value === '1' || value === 'true';
}

/** Pull a list out of `{ data: [] }`, `{ data: { data: [] } }`, or a bare array. */
function extractApiList(res) {
  const body = res?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  return null;
}

/**
 * Donate Essentials rows — only from API / DB (`campaign.essentials` → `ngo_campaign_essentials`).
 * No client-side fallback so the public page matches what is stored for the campaign.
 */
function getEssentialRows(campaign) {
  if (!campaign) return [];
  const raw = campaign.essentials || [];
  const active = raw.filter((e) => e && isEssentialActive(e.is_active));
  return active
    .slice()
    .sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0))
    .map((e) => ({
      id: String(e.id),
      label: e.item_name,
      unitPrice: Number(e.unit_price) || 0,
      description: e.description || null,
    }));
}

/** Extract iframe src from map_embed_url (handles full iframe HTML paste or plain URL) */
function getMapEmbedSrc(value) {
  if (!value || typeof value !== 'string') return '';
  const s = value.trim();
  const srcMatch = s.match(/src\s*=\s*["']([^"']+)["']/i);
  const url = srcMatch ? srcMatch[1].trim() : (s.startsWith('http://') || s.startsWith('https://') ? s : s);
  return url;
}

/** Sponsor tier / product row price from API (MySQL column or alternate key). */
function getCampaignProductPrice(p) {
  if (!p) return 0;
  const v = p.price ?? p.unit_price;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Many sites (e.g. google.com) set X-Frame-Options: sameorigin and cannot be embedded in iframes. */
function isEmbeddableMapUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    const path = u.pathname.toLowerCase();
    if (host === 'www.google.com' || host === 'google.com') {
      return path.startsWith('/maps/embed'); // only maps embed URLs are allowed to be framed
    }
    if (host.includes('openstreetmap.org') || host.includes('maps.google.com')) return true;
    return true; // allow other domains (e.g. mapbox, bing embed URLs)
  } catch (_) {
    return false;
  }
}

const CampaignDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const donateFormRef = useRef(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [donations, setDonations] = useState([]);
  const [donationsLoading, setDonationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [donationData, setDonationData] = useState({
    amount: '',
    email: '',
    name: '',
    surname: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    message: '',
    zipcode: '',
    pan_number: '',
    date_of_birth: '',
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_end_date: '',
  });
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [essentialsQty, setEssentialsQty] = useState({});
  const [formErrors, setFormErrors] = useState({
    amount: '', name: '', email: '', phone: '', address: '', pan_number: '', date_of_birth: '', sponsor: ''
  });
  const [submitError, setSubmitError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const amountInputRef = useRef(null);
  const donationVariant = campaign?.donation_variant || 'default';
  const AMOUNT_PRESETS_DEFAULT = [50, 100, 150, 200];
  const AMOUNT_PRESETS_CAMPAIGN = [500, 1000, 2000, 5000, 10000, 15000, 25000];
  const AMOUNT_PRESETS = donationVariant === 'default' ? AMOUNT_PRESETS_DEFAULT : AMOUNT_PRESETS_CAMPAIGN;

  const essentialRows = useMemo(() => getEssentialRows(campaign), [campaign]);

  const essentialsExtraTotal =
    donationVariant === 'school_kit' || donationVariant === 'freedom_hunger'
      ? essentialRows.reduce((sum, r) => sum + (Number(essentialsQty[r.id]) || 0) * r.unitPrice, 0)
      : 0;

  const hasSchoolKitProducts =
    donationVariant === 'school_kit' && (campaign?.products || []).length > 0;

  const sponsorTierPrice = useMemo(() => {
    if (!hasSchoolKitProducts || !selectedProductId || !campaign?.products?.length) return 0;
    const p = campaign.products.find((x) => Number(x.id) === Number(selectedProductId));
    return getCampaignProductPrice(p);
  }, [hasSchoolKitProducts, selectedProductId, campaign?.products]);

  const amountFieldNumeric = (() => {
    const n = parseFloat(donationData.amount);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  })();

  /** With sponsor tiers: amount field is extra on top of kit price; otherwise it is the full donation. */
  const baseDonationAmount = amountFieldNumeric;
  const paymentGrandTotal =
    (hasSchoolKitProducts ? sponsorTierPrice + baseDonationAmount : baseDonationAmount) +
    (donationVariant === 'school_kit' || donationVariant === 'freedom_hunger' ? essentialsExtraTotal : 0);

  const bumpEssential = (rowId, delta) => {
    setEssentialsQty((prev) => ({
      ...prev,
      [rowId]: Math.max(0, Math.min(99, (Number(prev[rowId]) || 0) + delta)),
    }));
  };

  const showError = (message) => {
    setSubmitError(message);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await campaignService.getCampaignBySlug(slug);
        let payload = response.data?.data || response.data || null;

        if (payload?.id) {
          try {
            const [productsRes, essentialsRes] = await Promise.all([
              campaignService.getCampaignProducts(payload.id).catch(() => null),
              campaignService.getCampaignEssentials(payload.id).catch(() => null),
            ]);
            const productsList = extractApiList(productsRes);
            if (Array.isArray(productsList)) {
              payload = { ...payload, products: productsList };
            }
            const essentialsList = extractApiList(essentialsRes);
            if (Array.isArray(essentialsList)) {
              payload = { ...payload, essentials: essentialsList };
            }
          } catch (e) {
            console.warn('Campaign extras fetch (products/essentials):', e);
          }
        }

        setCampaign(payload);
      } catch (error) {
        console.error('Error fetching campaign:', error);
        setCampaign(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [slug]);

  useEffect(() => {
    if (!campaign?.id) return;
    setSelectedProductId(null);
    if (!['school_kit', 'freedom_hunger'].includes(campaign.donation_variant || 'default')) {
      setEssentialsQty({});
      return;
    }
    const rows = getEssentialRows(campaign);
    setEssentialsQty(Object.fromEntries(rows.map((r) => [r.id, 0])));
  }, [campaign?.id, campaign?.donation_variant, campaign?.essentials]);

  useEffect(() => {
    if (!campaign?.id || activeTab !== 3) return;
    const fetchDonations = async () => {
      setDonationsLoading(true);
      try {
        const res = await donationService.getDonationsByCampaign(campaign.id);
        const data = res.data?.data ?? res.data ?? [];
        setDonations(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error fetching donations:', e);
        setDonations([]);
      } finally {
        setDonationsLoading(false);
      }
    };
    fetchDonations();
  }, [campaign?.id, activeTab]);

  const handleDonationChange = (e) => {
    const { name, value } = e.target;
    setDonationData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleDonationSubmit = async () => {
    const variant = campaign?.donation_variant || 'default';
    const fullName = [donationData.name, donationData.surname].filter(Boolean).join(' ').trim() || donationData.name;
    const amountNum = parseFloat(donationData.amount);
    const extraDonation = Number.isFinite(amountNum) && amountNum >= 0 ? amountNum : 0;
    const extras = variant === 'school_kit' || variant === 'freedom_hunger' ? essentialsExtraTotal : 0;
    const products = campaign.products || [];
    const kitPrice =
      variant === 'school_kit' && products.length > 0 && selectedProductId
        ? getCampaignProductPrice(products.find((x) => Number(x.id) === Number(selectedProductId)))
        : 0;
    const totalAmount =
      variant === 'school_kit' && products.length > 0
        ? kitPrice + extraDonation + extras
        : (Number.isFinite(amountNum) ? amountNum : 0) + extras;
    const errors = {};
    if (totalAmount < 1) {
      errors.amount =
        variant === 'school_kit' && products.length > 0
          ? 'Total must be at least ₹1 (sponsor kit + optional amount + essentials).'
          : 'Please enter a valid amount (min ₹1).';
    }
    if (!donationData.name?.trim()) errors.name = 'Name is required.';
    if (!donationData.email?.trim()) errors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(donationData.email.trim())) errors.email = 'Please enter a valid email address.';
    if (!donationData.phone?.trim()) errors.phone = 'Phone number is required.';
    else if (donationData.phone.trim().length < 10) errors.phone = 'Please enter a valid phone number (at least 10 digits).';

    const pan = (donationData.pan_number || '').trim().toUpperCase();
    const panOk = /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);
    if (variant === 'school_kit' || variant === 'freedom_hunger') {
      if (!donationData.address?.trim() || donationData.address.trim().length < 5) {
        errors.address = 'Full address is required (for 80G / receipts).';
      }
      if (!panOk) errors.pan_number = 'Valid PAN is required (format ABCDE1234F).';
    }
    if (variant === 'freedom_hunger') {
      if (!donationData.date_of_birth?.trim()) errors.date_of_birth = 'Date of birth is required.';
    }
    if (variant === 'school_kit' && (campaign.products || []).length > 0 && !selectedProductId) {
      errors.sponsor = 'Please choose how many children you would like to sponsor (tier below).';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...errors }));
      donateFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (errors.amount && amountInputRef.current) amountInputRef.current.focus();
      return;
    }
    setFormErrors({
      amount: '', name: '', email: '', phone: '', address: '', pan_number: '', date_of_birth: '', sponsor: ''
    });
    setSubmitError(null);

    try {
      const fullNameFinal = fullName;

      // First create/get user by email
      const userResponse = await donationService.getOrCreateUser({
        name: fullNameFinal,
        email: donationData.email,
        phone: donationData.phone,
        address: donationData.address,
        city: donationData.city,
        state: donationData.state,
        zipcode: donationData.zipcode,
        pan_number: pan,
        date_of_birth: variant === 'freedom_hunger' ? donationData.date_of_birth : undefined
      });

      const userId = userResponse.data?.userId || userResponse.data?.data?.id || userResponse.data?.id;

      if (!userId) {
        console.log('User response:', userResponse.data);
        showError('Unable to save your details. Please try again.');
        return;
      }

      const isExistingUser = userResponse.data?.message?.includes('already exists') || userResponse.data?.message?.includes('updated');
      console.log(isExistingUser ? `✅ Using existing user ID: ${userId}` : `✅ New user created with ID: ${userId}`);

      // Then initiate donation with user_id
      const notesParts = [];
      if (donationData.message?.trim()) notesParts.push(donationData.message.trim());
      if (variant === 'school_kit' && selectedProductId && (campaign.products || []).length > 0) {
        const p = (campaign.products || []).find((x) => Number(x.id) === Number(selectedProductId));
        if (p) notesParts.push(`Sponsor tier: ${p.product_name}`);
      }
      if (variant === 'school_kit' || variant === 'freedom_hunger') {
        const rows = getEssentialRows(campaign);
        const essLines = rows
          .filter((r) => (Number(essentialsQty[r.id]) || 0) > 0)
          .map((r) => `${r.label} × ${essentialsQty[r.id]} @ ₹${r.unitPrice}`);
        if (essLines.length) notesParts.push(`Donate Essentials: ${essLines.join('; ')}`);
      }
      const notesPayload = notesParts.length ? notesParts.join('\n') : null;

      const donationResponse = await donationService.initiateDonation({
        user_id: userId,
        campaign_id: campaign.id,
        amount: totalAmount,
        payment_method: 'card', // actual method is chosen in Razorpay Checkout
        is_recurring: donationData.is_recurring,
        recurring_frequency: donationData.is_recurring ? donationData.recurring_frequency : null,
        recurring_end_date: donationData.is_recurring && donationData.recurring_end_date ? donationData.recurring_end_date : null,
        campaign_product_id: variant === 'school_kit' && (campaign.products || []).length > 0 ? selectedProductId : null,
        notes: notesPayload
      });

      const donationId = donationResponse.data?.donationId;
      const paymentData = donationResponse.data?.paymentData;
      const isSubscription = !!(paymentData?.isSubscription && paymentData?.subscriptionId);

      if (!donationId || !paymentData || (!paymentData.orderId && !paymentData.subscriptionId)) {
        showError('Unable to start payment. Please try again.');
        return;
      }

      // Mock flow only when API explicitly marks the order as mock
      const isMockMode = paymentData.mockMode === true;
      
      if (isMockMode) {
        // Mock mode: Simulate payment without Razorpay popup
        console.log('🧪 MOCK MODE: Simulating payment');
        
        // Simulate payment success after a short delay
        setTimeout(async () => {
          try {
            const mockPaymentId = `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const mockSignature = 'mock_signature_for_testing';
            
            // Verify payment on backend
            await donationService.verifyDonation({
              donationId: donationId,
              transactionId: mockPaymentId,
              paymentVerification: isSubscription
                ? {
                    razorpay_subscription_id: paymentData.subscriptionId,
                    razorpay_payment_id: mockPaymentId,
                    razorpay_signature: mockSignature,
                  }
                : {
                    razorpay_order_id: paymentData.orderId,
                    razorpay_payment_id: mockPaymentId,
                    razorpay_signature: mockSignature,
                  },
            });
            
            // Redirect to success page
            navigate(`/donation/status?status=success&donationId=${donationId}&transactionId=${mockPaymentId}`);
          } catch (error) {
            console.error('Payment verification error:', error);
            // Redirect to failed page
            navigate(`/donation/status?status=failed&donationId=${donationId}`);
          }
        }, 1000);
        return;
      }

      // Real Razorpay mode
      const razorpayKey = paymentData.razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
      console.log('Razorpay Key:', razorpayKey);
      console.log('Payment Data:', paymentData);
      console.log('Donation ID:', donationId);
      
      if (!razorpayKey) {
        showError('Payment is not configured. Please contact support.');
        return;
      }

      if (!paymentData.amount) {
        showError('Invalid payment amount. Please try again.');
        return;
      }

      const options = {
        key: razorpayKey,
        name: 'NGO Donation',
        description: isSubscription
          ? `Recurring donation to ${campaign.title}`
          : `Donation to ${campaign.title}`,
        theme: {
          color: BRAND.green
        },
        prefill: {
          name: fullNameFinal,
          email: donationData.email,
          contact: donationData.phone
        },
        handler: async (response) => {
          try {
            // Verify payment on backend (order vs subscription signature differs)
            await donationService.verifyDonation({
              donationId: donationId,
              transactionId: response.razorpay_payment_id,
              paymentVerification: response.razorpay_subscription_id
                ? {
                    razorpay_subscription_id: response.razorpay_subscription_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                  }
                : {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                  },
            });
            
            // Redirect to success page
            navigate(`/donation/status?status=success&donationId=${donationId}&transactionId=${response.razorpay_payment_id}`);
          } catch (error) {
            console.error('Payment verification error:', error);
            // Redirect to failed page
            navigate(`/donation/status?status=failed&donationId=${donationId}`);
          }
        },
        modal: {
          ondismiss: async () => {
            try {
              await donationService.failDonation(donationId, 'Payment cancelled');
            } catch (_) {
              /* ignore */
            }
            navigate(`/donation/status?status=failed&donationId=${donationId}&reason=${encodeURIComponent('Payment cancelled')}`);
          }
        },
        onerror: async (response) => {
          console.error('Razorpay Error:', response);
          const reason =
            response?.error?.description ||
            response?.error?.reason ||
            response?.error?.code ||
            'Payment failed';
          try {
            await donationService.failDonation(donationId, reason);
          } catch (_) {
            /* ignore */
          }
          navigate(
            `/donation/status?status=failed&donationId=${donationId}&reason=${encodeURIComponent(reason)}`
          );
        }
      };

      if (isSubscription) {
        options.subscription_id = paymentData.subscriptionId;
      } else {
        options.amount = paymentData.amount;
        options.currency = paymentData.currency || 'INR';
        options.order_id = paymentData.orderId;
        options.receipt = paymentData.receipt || `donation_${donationId}`;
      }

      console.log('Opening Razorpay with options:', options);
      if (!window.Razorpay) {
        showError('Payment gateway did not load. Please refresh the page and try again.');
        return;
      }

      try {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } catch (err) {
        console.error('Error opening Razorpay:', err);
        showError('Could not open payment window: ' + (err.message || 'Please try again.'));
      }
    } catch (error) {
      console.error('Error initiating donation:', error);
      const apiMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Something went wrong. Please try again.';
      showError(apiMsg);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: BRAND.green }} />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Container maxWidth={false} sx={{ width: '100%', px: 3, py: 8 }}>
        <Typography variant="h6">Campaign not found</Typography>
      </Container>
    );
  }

  const targetAmount = Number(campaign.target_amount) || 0;
  const achievedAmount = Number(campaign.stats?.total_amount ?? campaign.current_amount ?? 0) || 0;
  const percentFunded = targetAmount > 0 ? Math.min(100, Math.round((achievedAmount / targetAmount) * 100)) : 0;
  const donorCount = Number(campaign.stats?.unique_donors ?? 0);

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <ImageHeroBanner
        subtitle={campaign.category?.name || 'Campaign'}
        title={campaign.title || campaign.name || 'Campaign'}
        description={(() => {
          const text =
            campaign.short_description ||
            campaign.description ||
            campaign.content ||
            'Your support helps create lasting change.';
          return text.length > 200 ? `${text.slice(0, 200)}…` : text;
        })()}
        backgroundImage={getImageUrl(campaign.image_url) || ASSETS.cause1}
        buttonText="Donate Now"
        onButtonClick={() => donateFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        minHeight={{ xs: 440, sm: 500, md: 520 }}
        maxHeight={{ md: 600 }}
      >
        <CampaignShareButtons campaignSlug={campaign.slug} title={campaign.title} iconSize={32} showLabel={false} />
      </ImageHeroBanner>

      {/* Progress / stats bar: goal, achieved, %, donations, Donate Now (matches attached design) */}
      <Box sx={{ borderTop: `2px solid ${STATS_BAR_BORDER}`, backgroundColor: STATS_BAR_BG, color: '#fff', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', alignItems: 'stretch', minHeight: 80 }}>
        {/* Left: goal + achieved progress strip; purple has minWidth so amount is never cut off */}
        <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 45%' }, maxWidth: { sm: 560 }, minHeight: 76, display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
          <Box sx={{ width: `${Math.max(percentFunded, 8)}%`, minWidth: 240, backgroundColor: PURPLE_ACCENT, display: 'flex', alignItems: 'center', pl: 3, pr: 3, py: 2.5, transition: 'width 0.3s ease', overflow: 'hidden' }}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#fff', mb: 0.25 }}>GOAL CAUSE</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 18 }, textDecoration: 'underline', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={targetAmount.toLocaleString('en-IN')}>₹{targetAmount.toLocaleString('en-IN')}</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', pl: 2, pr: 3, py: 2.5, overflow: 'hidden' }}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#fff', mb: 0.25 }}>ACHIEVED</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 18 }, textDecoration: 'underline', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={achievedAmount.toLocaleString('en-IN')}>₹{achievedAmount.toLocaleString('en-IN')}</Typography>
            </Box>
          </Box>
        </Box>
        {/* Middle: 58% and Donations with spacing */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'center' }, gap: { xs: 4, sm: 6 }, pl: { xs: 3, sm: 4 }, pr: { xs: 3, sm: 4 }, py: 2.5, minHeight: 76 }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 24, sm: 28 }, lineHeight: 1.1, color: '#fff', letterSpacing: '0.02em' }}>{percentFunded} %</Typography>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: STATS_LABEL_GRAY, mt: 0.25 }}>OF OUR GOAL</Typography>
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 24, sm: 28 }, lineHeight: 1.1, color: '#fff', letterSpacing: '0.02em' }}>{donorCount}</Typography>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: STATS_LABEL_GRAY, mt: 0.25 }}>DONATIONS</Typography>
          </Box>
        </Box>
        {/* Right: Donate Now button */}
        <Box sx={{ flex: { xs: '0 0 auto', sm: '0 0 auto' }, display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, pr: 3, pl: { xs: 3, sm: 0 }, pb: { xs: 2, sm: 0 }, pt: { xs: 0, sm: 0 } }}>
          <Button
            variant="contained"
            onClick={() => donateFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            sx={{ backgroundColor: '#fff', color: '#2d2d2d', fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', borderRadius: 28, px: 3.5, py: 2, minHeight: 48, '&:hover': { backgroundColor: '#f0f0f0' } }}
          >
            Donate Now
          </Button>
        </Box>
      </Box>

      {/* Quote block: image left, purple panel right, progress bar at bottom (matches design) */}
      <Box sx={{ py: { xs: 4, md: 5 }, px: { xs: 0, sm: 2 }, backgroundColor: '#FAF9F6' }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 0, sm: 2 } }}>
          <Box sx={{ position: 'relative', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { xs: 380, md: 420 }, overflow: 'hidden', borderRadius: { xs: 0, md: 0 }, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            {/* Left: image ~60–65% */}
            <Box sx={{ flex: { xs: '0 0 55%', md: '0 0 62%' }, minHeight: { xs: 240, md: 420 } }}>
              <Box component="img" src={getImageUrl(campaign.image_url) || ASSETS.image08} alt="" sx={{ width: '100%', height: '100%', minHeight: { xs: 240, md: 420 }, objectFit: 'cover', display: 'block' }} />
            </Box>
            {/* Right: purple quote panel – deep purple, quote top-left, text centered */}
            <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 38%' }, backgroundColor: QUOTE_PANEL_PURPLE, color: '#fff', p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', position: 'relative', minHeight: { xs: 220, md: 420 } }}>
              <FormatQuote sx={{ position: 'absolute', top: 28, left: 28, fontSize: 64, color: '#fff', opacity: 1 }} />
              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: { xs: 17, md: 20 }, lineHeight: 1.5, color: '#fff', mb: 2.5 }}>
                  {campaign.description || 'Protecting and restoring natural forests'}
                </Typography>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 12, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#fff' }}>
                  ROOTS & WINGS FOUNDATION
                </Typography>
              </Box>
            </Box>
            {/* Progress bar at bottom: full width, overlapping both sections */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 10, display: 'flex', alignItems: 'stretch', zIndex: 2 }}>
              <Box sx={{ width: `${percentFunded}%`, backgroundColor: PURPLE_LIGHT, transition: 'width 0.3s ease' }} />
              <Box sx={{ flex: 1, backgroundColor: PROGRESS_GRAY }} />
              <Box
                sx={{
                  position: 'absolute',
                  left: `${percentFunded}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: PURPLE_ACCENT,
                  color: '#fff',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 700,
                  fontSize: 15,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}
              >
                {percentFunded}%
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Tabs: Description, Documents, Map, Donations */}
      <Box sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, sm: 3 }, backgroundColor: '#fff' }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: 0 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '2px solid #e5e7eb', mb: 3, '& .MuiTab-root': { fontFamily: '"Poppins", sans-serif', fontWeight: 600 } }}>
            <Tab label="Description" />
            <Tab label="Documents" />
            <Tab label="Map" />
            <Tab label="Donations" />
            <Tab label="Ratings" />
          </Tabs>
          {activeTab === 0 && (
            <Typography sx={{ ...bodyTextSx, whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
              {campaign.content || campaign.description || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.'}
            </Typography>
          )}
          {activeTab === 1 && (
            <Box sx={{ width: '100%' }}>
              {campaign.documents && campaign.documents.length > 0 ? (
                <Grid container spacing={2}>
                  {campaign.documents.map((doc) => {
                    const docUrl = getApiOrigin() + doc.file_url;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={doc.id}>
                        <Box
                          sx={{
                            ...cardSx,
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            borderLeft: `4px solid ${BRAND.green}`,
                            backgroundColor: COLORS.warm,
                          }}
                        >
                          <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: BRAND.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <InsertDriveFile sx={{ color: '#fff', fontSize: 28 }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ ...cardHeadingSx, fontSize: 16, mb: 1 }}>{doc.title}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => window.open(docUrl, 'DocumentView', 'width=900,height=700,scrollbars=yes,resizable=yes')}
                                sx={{ borderColor: BRAND.green, color: BRAND.green }}
                              >
                                View
                              </Button>
                              <Button size="small" variant="outlined" href={docUrl} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNew />} sx={{ borderColor: BRAND.green, color: BRAND.green }}>
                                Open
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography sx={{ ...bodyTextSx, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  No documents for this campaign yet.
                </Typography>
              )}
            </Box>
          )}
          {activeTab === 2 && (
            <Box sx={{ width: '100%' }}>
              {(() => {
                const mapSrc = getMapEmbedSrc(campaign.map_embed_url);
                if (mapSrc) {
                  if (!isEmbeddableMapUrl(mapSrc)) {
                    return (
                      <Box sx={{ py: 4, textAlign: 'center', backgroundColor: COLORS.cream, borderRadius: 2, p: 3 }}>
                        <Typography sx={{ ...bodyTextSx, mb: 2 }}>
                          This map link cannot be shown inside the page (the site blocks embedding). Use the button below to open it in a new tab.
                        </Typography>
                        <Button variant="contained" href={mapSrc} target="_blank" rel="noopener noreferrer" sx={{ ...primaryButtonSx, backgroundColor: BRAND.green }}>
                          Open map in new tab
                        </Button>
                        <Typography sx={{ ...bodyTextSx, mt: 2, fontSize: 13, color: 'text.secondary' }}>
                          Tip: For an embedded map, use Google Maps → Share → Embed a map, and paste the embed URL (contains /maps/embed).
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ width: '100%', height: { xs: 300, md: 450 }, borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                      <iframe title="Campaign location" src={mapSrc} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                    </Box>
                  );
                }
                if (campaign.map_lat != null && campaign.map_lng != null) {
                  return (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography sx={{ ...bodyTextSx, mb: 2 }}>{campaign.map_address || 'Location'}</Typography>
                      <Typography sx={{ ...bodyTextSx, color: 'text.secondary' }}>
                        Lat: {campaign.map_lat}, Lng: {campaign.map_lng}
                      </Typography>
                      <Typography sx={{ ...bodyTextSx, mt: 2, fontSize: 13 }} component="a" href={`https://www.google.com/maps?q=${campaign.map_lat},${campaign.map_lng}`} target="_blank" rel="noopener noreferrer" sx={{ color: BRAND.green }}>
                        Open in Google Maps
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Typography sx={{ ...bodyTextSx, py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    No map available for this campaign.
                  </Typography>
                );
              })()}
            </Box>
          )}
          {activeTab === 3 && (
            <Box sx={{ width: '100%' }}>
              {donationsLoading ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress sx={{ color: BRAND.green }} />
                </Box>
              ) : donations.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center', backgroundColor: COLORS.cream, borderRadius: 2, px: 3 }}>
                  <Typography sx={{ ...sectionSubtitleSx, color: COLORS.subtitle }}>No donations yet</Typography>
                  <Typography sx={{ ...bodyTextSx, mt: 1, color: COLORS.body }}>Be the first to support this campaign.</Typography>
                </Box>
              ) : (
                <>
                  <Typography sx={{ ...sectionSubtitleSx, mb: 1 }}>Our supporters</Typography>
                  <Typography sx={{ ...sectionHeadingSx, mb: 3, fontSize: { xs: 22, md: 28 } }}>Recent Donations</Typography>
                  <Grid container spacing={3}>
                    {donations.map((d, i) => {
                      const rawName = d.donor_name ?? d.donorName ?? d.name ?? d.full_name
                        ?? d.user?.name ?? d.user?.full_name ?? d.donor?.name
                        ?? (d.user?.first_name || d.user?.last_name ? [d.user.first_name, d.user.last_name].filter(Boolean).join(' ').trim() : null)
                        ?? (d.first_name || d.last_name ? [d.first_name, d.last_name].filter(Boolean).join(' ').trim() : null);
                      const name = (d.is_anonymous || d.anonymous) ? 'Anonymous' : (rawName || 'Anonymous');
                      const amount = d.amount ?? d.donation_amount ?? 0;
                      const date = d.created_at ?? d.createdAt ?? d.date ?? '';
                      const message = d.message ?? d.note ?? '';
                      const displayDate = date ? new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                      return (
                        <Grid item xs={12} sm={6} md={4} key={d.id ?? i}>
                          <Box
                            sx={{
                              ...cardSx,
                              p: 2.5,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.5,
                              backgroundColor: COLORS.warm,
                              borderLeft: `4px solid ${BRAND.green}`,
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: BRAND.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Favorite sx={{ color: '#fff', fontSize: 24 }} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ ...cardHeadingSx, fontSize: 18, color: COLORS.heading, mb: 0.5 }}>
                                  {name}
                                </Typography>
                                <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 800, color: BRAND.green, fontSize: 20 }}>
                                  ₹{Number(amount).toLocaleString('en-IN')}
                                </Typography>
                              </Box>
                            </Box>
                            {displayDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: COLORS.body }}>
                                <CalendarToday sx={{ fontSize: 16, color: BRAND.green }} />
                                <Typography sx={{ ...bodyTextSx, fontSize: 13 }}>{displayDate}</Typography>
                              </Box>
                            )}
                            {message && (
                              <Typography sx={{ ...bodyTextSx, fontSize: 13, fontStyle: 'italic', color: COLORS.body }} title={message}>
                                "{message}"
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </Box>
          )}
          {activeTab === 4 && (
            <CampaignRatingsSection
              campaignId={campaign.id}
              initialRatings={campaign.ratings}
              initialSummary={campaign.rating_summary}
            />
          )}
        </Container>
      </Box>

      {/* Donate Now section: full form (all fields from previous popup), scroll target for Donate Now buttons */}
      <Box ref={donateFormRef} sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, sm: 3 }, backgroundColor: '#F9F4E8', scrollMarginTop: 24 }}>
        <Container maxWidth="md" sx={{ width: '100%', px: 0 }}>
          <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: 22, color: '#2D2929', mb: 3 }}>
            DONATE NOW
          </Typography>

          {(donationVariant === 'school_kit' || donationVariant === 'freedom_hunger') && (
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e8e0d0', backgroundColor: 'rgba(255,255,255,0.85)' }}>
              <Typography sx={{ ...bodyTextSx, fontWeight: 700, mb: 1 }}>Donations &amp; 80G</Typography>
              <Typography sx={{ ...bodyTextSx, fontSize: 14, mb: 1 }}>
                Contributions may qualify for deduction under Section 80G of the Income Tax Act, where applicable. Please provide accurate PAN and address for receipt issuance.
              </Typography>
              <Typography sx={{ ...bodyTextSx, fontSize: 13, color: 'text.secondary' }}>
                You will choose your payment method (Card, UPI, Net Banking, etc.) in the secure Razorpay checkout. Confirmation is sent after successful payment.
              </Typography>
            </Paper>
          )}

          {donationVariant === 'school_kit' && (
            <Box sx={{ mb: 3 }}>
              {(campaign.products || []).length > 0 ? (
                <>
                  <Box
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid #b8dfd9',
                      boxShadow: '0 4px 18px rgba(13, 138, 126, 0.12)',
                      backgroundColor: '#fafcfb',
                    }}
                  >
                    <Box
                      sx={{
                        background: 'linear-gradient(180deg, #e8f7f4 0%, #dff2ee 100%)',
                        py: 1.75,
                        px: 2,
                        textAlign: 'center',
                        borderBottom: '2px solid #9dd5cd',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 800,
                          fontSize: { xs: 15, sm: 17 },
                          letterSpacing: '0.14em',
                          color: '#0d7d72',
                          lineHeight: 1.3,
                        }}
                      >
                        CHILD EDUCATION SUPPORT
                      </Typography>
                    </Box>
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
                      <Typography sx={{ ...bodyTextSx, fontSize: 14, mb: 2, color: 'text.secondary', textAlign: 'center' }}>
                        Sponsor a kit for one or more children. Tap a card to select your tier. Use “Additional donation” below if you wish to give more; essentials add on separately.
                      </Typography>
                      {formErrors.sponsor && (
                        <Alert severity="warning" sx={{ mb: 2 }}>{formErrors.sponsor}</Alert>
                      )}
                      <Grid container spacing={1.5}>
                        {(campaign.products || []).map((p, idx) => {
                          const selected = Number(selectedProductId) === Number(p.id);
                          const cardBg = SPONSOR_TIER_CARD_BGS[idx % SPONSOR_TIER_CARD_BGS.length];
                          const priceNum = getCampaignProductPrice(p);
                          const subtitle =
                            p.description && String(p.description).trim()
                              ? p.description
                              : `Monthly requirement — Rs. ${priceNum.toLocaleString('en-IN')}/-`;
                          return (
                            <Grid item xs={12} sm={6} md={4} key={p.id}>
                              <Paper
                                elevation={selected ? 4 : 0}
                                onClick={() => {
                                  setSelectedProductId(p.id);
                                  setFormErrors((prev) => ({ ...prev, sponsor: '' }));
                                }}
                                sx={{
                                  p: 2.25,
                                  minHeight: 120,
                                  cursor: 'pointer',
                                  borderRadius: 2,
                                  border: selected ? `3px solid ${BRAND.green}` : '1px solid rgba(0,0,0,0.08)',
                                  backgroundColor: selected ? `${cardBg}` : cardBg,
                                  boxShadow: selected ? `0 0 0 3px rgba(36, 136, 78, 0.25)` : 'none',
                                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  '&:hover': {
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: '"Poppins", sans-serif',
                                    fontWeight: 800,
                                    fontSize: { xs: 12, sm: 13 },
                                    letterSpacing: '0.04em',
                                    lineHeight: 1.35,
                                    textAlign: 'center',
                                    color: '#1a1a1a',
                                    mb: 1.25,
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {(p.product_name || 'Sponsor kit').trim()}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: '"Nunito", sans-serif',
                                    fontSize: { xs: 13, sm: 14 },
                                    fontWeight: 700,
                                    textAlign: 'center',
                                    color: '#2d2d2d',
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {subtitle}
                                </Typography>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', border: '1px solid #b8dfd9' }}>
                  <Box
                    sx={{
                      background: 'linear-gradient(180deg, #e8f7f4 0%, #dff2ee 100%)',
                      py: 1.75,
                      px: 2,
                      textAlign: 'center',
                      borderBottom: '2px solid #9dd5cd',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 800,
                        fontSize: { xs: 15, sm: 17 },
                        letterSpacing: '0.14em',
                        color: '#0d7d72',
                      }}
                    >
                      CHILD EDUCATION SUPPORT
                    </Typography>
                  </Box>
                  <Alert severity="info" sx={{ m: 2 }}>
                    Sponsor tiers are not set up for this campaign yet. Add products in Admin → Campaigns → Edit, or seed <code>ngo_campaign_products</code>.
                  </Alert>
                </Box>
              )}
              <DonateEssentialsBlock
                essentialRows={essentialRows}
                essentialsQty={essentialsQty}
                bumpEssential={bumpEssential}
                essentialsExtraTotal={essentialsExtraTotal}
                description="Add optional quantities for extra supplies. Tap a card or use − and + to adjust. These amounts are added to your donation total."
                sx={{ mt: (campaign.products || []).length > 0 ? 3 : 0 }}
              />
            </Box>
          )}

          {donationVariant === 'freedom_hunger' && essentialRows.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <DonateEssentialsBlock
                essentialRows={essentialRows}
                essentialsQty={essentialsQty}
                bumpEssential={bumpEssential}
                essentialsExtraTotal={essentialsExtraTotal}
                description="Add optional nutrition quantities. Tap a card or use − and + to adjust. These amounts are added to your donation total."
              />
            </Box>
          )}

          {Object.values(formErrors).some(Boolean) && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFormErrors({ amount: '', name: '', email: '', phone: '', address: '', pan_number: '', date_of_birth: '', sponsor: '' })}>
              Please fix the errors below and try again.
            </Alert>
          )}

          <Typography sx={{ ...bodyTextSx, mb: 1 }}>
            {hasSchoolKitProducts ? (
              <>
                Additional donation (₹){' '}
                <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 400 }}>
                  optional — sponsor kit price is added in the payment summary below
                </Typography>
              </>
            ) : (
              <>Donation Amount (₹) *</>
            )}
            {(donationVariant === 'school_kit' || donationVariant === 'freedom_hunger') && essentialsExtraTotal > 0 && (
              <Typography component="span" sx={{ ml: 1, fontSize: 13, color: 'text.secondary' }}>
                (essentials add ₹{essentialsExtraTotal.toLocaleString('en-IN')} to your total)
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {AMOUNT_PRESETS.map((amt) => (
              <Button key={amt} variant="outlined" onClick={() => { setDonationData((p) => ({ ...p, amount: String(amt) })); if (formErrors.amount) setFormErrors(p => ({ ...p, amount: '' })); }} sx={{ minWidth: 70, borderColor: donationData.amount === String(amt) ? BRAND.green : '#ccc', color: donationData.amount === String(amt) ? BRAND.green : '#333' }}>{amt}</Button>
            ))}
            <TextField inputRef={amountInputRef} size="small" placeholder={hasSchoolKitProducts ? '0' : 'Custom amount'} type="number" name="amount" value={donationData.amount} onChange={handleDonationChange} sx={{ width: 140 }} inputProps={{ min: hasSchoolKitProducts ? 0 : 1, step: 'any' }} error={!!formErrors.amount} helperText={formErrors.amount} FormHelperTextProps={{ sx: { mt: 0.5 } }} />
          </Box>

          <Typography sx={{ ...bodyTextSx, mb: 2 }}>Your details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name *" name="name" value={donationData.name} onChange={handleDonationChange} required error={!!formErrors.name} helperText={formErrors.name} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Surname" name="surname" value={donationData.surname} onChange={handleDonationChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Email *" name="email" type="email" value={donationData.email} onChange={handleDonationChange} required error={!!formErrors.email} helperText={formErrors.email} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Phone *" name="phone" type="tel" value={donationData.phone} onChange={handleDonationChange} required placeholder="+91 XXXXX XXXXX" error={!!formErrors.phone} helperText={formErrors.phone} /></Grid>
            <Grid item xs={12}><TextField fullWidth label={donationVariant === 'school_kit' || donationVariant === 'freedom_hunger' ? 'Address *' : 'Address'} name="address" value={donationData.address} onChange={handleDonationChange} multiline rows={2} required={donationVariant === 'school_kit' || donationVariant === 'freedom_hunger'} error={!!formErrors.address} helperText={formErrors.address} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="City" name="city" value={donationData.city} onChange={handleDonationChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="State" name="state" value={donationData.state} onChange={handleDonationChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Zip Code" name="zipcode" value={donationData.zipcode} onChange={handleDonationChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label={donationVariant === 'school_kit' || donationVariant === 'freedom_hunger' ? 'PAN Number *' : 'PAN Number'} name="pan_number" value={donationData.pan_number} onChange={handleDonationChange} placeholder="ABCDE1234F" inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }} helperText={formErrors.pan_number || (donationVariant === 'default' ? 'Optional, for 80G receipt' : 'Required for 80G receipt')} error={!!formErrors.pan_number} required={donationVariant === 'school_kit' || donationVariant === 'freedom_hunger'} /></Grid>
            {donationVariant === 'freedom_hunger' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of birth *"
                  name="date_of_birth"
                  type="date"
                  value={donationData.date_of_birth}
                  onChange={handleDonationChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!!formErrors.date_of_birth}
                  helperText={formErrors.date_of_birth}
                />
              </Grid>
            )}
            <Grid item xs={12}><TextField fullWidth label="Message (optional)" name="message" value={donationData.message} onChange={handleDonationChange} multiline rows={2} /></Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.5)' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={donationData.is_recurring}
                  onChange={(e) => setDonationData((p) => ({ ...p, is_recurring: e.target.checked }))}
                  name="is_recurring"
                />
              }
              label={<Typography sx={{ fontWeight: 600, color: BRAND.green, fontFamily: '"Poppins", sans-serif' }}>Make this a recurring donation</Typography>}
            />
            {donationData.is_recurring && (
              <Box sx={{ mt: 2, ml: { xs: 0, sm: 4 } }}>
                <TextField fullWidth select label="Recurring frequency" name="recurring_frequency" value={donationData.recurring_frequency} onChange={handleDonationChange} sx={{ mb: 2 }} SelectProps={{ native: true }}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly (Every 3 months)</option>
                  <option value="yearly">Yearly</option>
                </TextField>
                <TextField fullWidth label="End date (optional)" name="recurring_end_date" type="date" value={donationData.recurring_end_date} onChange={handleDonationChange} InputLabelProps={{ shrink: true }} helperText="Leave empty for ongoing" />
              </Box>
            )}
          </Box>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              mb: 2,
              p: 2.5,
              borderRadius: 2,
              border: '1px solid #e8e0d0',
              backgroundColor: 'rgba(255,255,255,0.95)',
            }}
          >
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 16, mb: 2, color: '#2D2929' }}>
              Payment summary
            </Typography>
            {hasSchoolKitProducts && sponsorTierPrice > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                <Typography sx={{ ...bodyTextSx, color: '#444' }}>Sponsor kit (selected tier)</Typography>
                <Typography sx={{ ...bodyTextSx, fontWeight: 700, fontFamily: '"Nunito", sans-serif' }}>
                  ₹{sponsorTierPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
              <Typography sx={{ ...bodyTextSx, color: '#444' }}>
                {hasSchoolKitProducts ? 'Additional donation' : 'Donation amount'}
              </Typography>
              <Typography sx={{ ...bodyTextSx, fontWeight: 700, fontFamily: '"Nunito", sans-serif' }}>
                ₹{baseDonationAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </Typography>
            </Box>
            {(donationVariant === 'school_kit' || donationVariant === 'freedom_hunger') && essentialsExtraTotal > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                <Typography sx={{ ...bodyTextSx, color: '#444' }}>Donate Essentials</Typography>
                <Typography sx={{ ...bodyTextSx, fontWeight: 700, fontFamily: '"Nunito", sans-serif' }}>
                  ₹{essentialsExtraTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pt: 2,
                mt: 1,
                borderTop: `2px solid ${BRAND.green}`,
              }}
            >
              <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: 18, color: '#2D2929' }}>
                Total to pay
              </Typography>
              <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: 22, color: BRAND.green }}>
                ₹{paymentGrandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" onClick={handleDonationSubmit} sx={{ ...primaryButtonSx, px: 4, backgroundColor: PURPLE_ACCENT, '&:hover': { backgroundColor: '#6d28d9' } }}>
              Proceed to Payment
            </Button>
          </Box>
          <Typography sx={{ ...bodyTextSx, fontSize: 12, textAlign: 'center', mt: 2 }}>Your donations are eligible for 80G tax deduction. Payment via Credit Card, UPI, Net Banking.</Typography>
        </Container>
      </Box>

      {/* Join With Us - same as HomePage: single bar with parallax bg, overlay, 4 stats */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography
            sx={{
              fontFamily: '"Caveat", cursive',
              fontWeight: 700,
              fontSize: { xs: 24, md: 27 },
              lineHeight: '100%',
              letterSpacing: 0,
              color: '#313530',
              textAlign: 'center',
              mb: '25px',
            }}
          >
            Join With Us
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Nunito", sans-serif',
              fontWeight: 900,
              fontSize: { xs: 26, sm: 34, md: 40 },
              lineHeight: '1.25em',
              letterSpacing: '0.01em',
              color: '#2D2929',
              textAlign: 'center',
              mb: { xs: '30px', md: '50px' },
            }}
          >
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 1200,
              mx: 'auto',
              borderRadius: { xs: 16, md: '23px' },
              minHeight: { xs: 200, md: 238 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #c4a574',
              backgroundImage: `url(${ASSETS.parallax})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(36, 88, 78, 0.63)',
                borderRadius: '23px',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: 200, md: 238 },
                gap: { xs: 3, sm: 0 },
                py: { xs: 4, md: 5 },
                px: { xs: 3, md: 4 },
              }}
            >
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ '&.MuiSnackbar-root': { bottom: 24 } }}>
        <Alert elevation={6} variant="filled" onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%', fontFamily: '"Poppins", sans-serif' }}>
          {submitError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignDetailPage;
